<template>
    <div
        v-for="(item, itemIndex) in dataComputed"
        :key="itemIndex"
        class="working-hours-item"
    >
        {{ weekdayFormatter.format(parseWeekDay(item.day.from)) }}
        <span v-if="item.day.to">
            - {{ weekdayFormatter.format(parseWeekDay(item.day.to)) }}
        </span>
        :
        <!-- https://developer.mozilla.org/en-US/docs/Web/HTML/Element/time -->
        <!-- 18:00:00Z is not a valid time string according to MDN -->
        <time :datetime="item.hours.from.replace('Z', '')">
            {{ timeFormatter.format(parseTime(item.hours.from)) }}
        </time>
        -
        <time :datetime="item.hours.to.replace('Z', '')">
            {{ timeFormatter.format(parseTime(item.hours.to)) }}
        </time>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { WeekDay, WorkingHoursFragment } from '@/graphql/api'
import { useI18n } from 'vue-i18n'
import { isNotUndefinedOrNull } from '@/helpers'

type WorkingHoursFormatted = {
    day: {
        from: WeekDay
        to: WeekDay | undefined
    }
    hours: {
        from: string
        to: string
    }
}

const props = defineProps<{
    data: WorkingHoursFragment[]
}>()

// TODO Move parses somewhere, ideally as urql exchange
// TODO what do with TZ ?
const { locale } = useI18n()
const weekdayFormatter = computed(
    () =>
        new Intl.DateTimeFormat(locale.value, {
            weekday: 'short',
        })
)

const parseWeekDay = (weekday: WeekDay): Date => {
    const date = new Date()
    const currentDay = date.getDay()

    const days: WeekDay[] = [
        'SUNDAY',
        'MONDAY',
        'TUESDAY',
        'WEDNESDAY',
        'THURSDAY',
        'FRIDAY',
        'SATURDAY',
    ]
    const weekdayIndex = days.indexOf(weekday)
    date.setDate(date.getDate() + (weekdayIndex - currentDay))

    return date
}

const parseTime = (time: string): Date => {
    const currentDateString = new Date().toISOString()

    return new Date(
        currentDateString.substring(0, currentDateString.indexOf('T') + 1) +
            time
    )
}

const dataComputed = computed((): WorkingHoursFormatted[] =>
    props.data
        .map((item): WorkingHoursFormatted | undefined => {
            const fromDay = item.weekDays[0]

            if (!fromDay) {
                return undefined
            }

            const toDay = item.weekDays[-1]

            return {
                day: {
                    from: fromDay,
                    to: toDay,
                },
                hours: {
                    from: item.opening,
                    to: item.closing,
                },
            }
        })
        .filter(isNotUndefinedOrNull)
)

const timeFormatter = computed(() => {
    return new Intl.DateTimeFormat(locale.value, {
        hour: 'numeric',
        minute: '2-digit',
    })
})
</script>
<style lang="scss" scoped>
.working-hours-item {
    display: block;
}
</style>
